// src/admin/pages/sold-vehicles/SoldVehiclesManagement.tsx
// The one place that lists every sold vehicle — imported or pre-owned —
// with full buyer/payment details, and the only place a sale is recorded.
// See soldVehicleService.ts and answers.text for why this exists separately
// from Reservations (which stays reference-only) and CarDrawer
// (which no longer allows picking 'Sold' directly).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  InputAdornment,
  Pagination,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon, Search as SearchIcon, Sell as SellIcon } from '@mui/icons-material';
import { useAuth } from '../../../contexts/AuthContext';
import { useCompany } from '../../../contexts/CompanyContext';
import { ProductService } from '../car/carService';
import { Product } from '../../../enums/product';
import { ReservationService } from '../reservation/reservationService';
import { SoldVehicleService } from './soldVehicleService';
import { TradeInService } from '../tradein/tradeInService';
import { TradeInSubmission } from '../../../enums/tradeIn';
import { EmployeeService } from '../employee/employeeService';
import { Employee } from '../../../enums/employee';
import { SaleReceiptGenerator } from './saleReceiptGenerator';
import SoldVehicleTable from './SoldVehicleTable';
import SoldVehicleDialog, { SoldVehicleFormValues } from './SoldVehicleDialog';
import ToastNotification from '../common/Toastnotification';

const PER_PAGE = 10;

interface ToastState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const SoldVehiclesManagement: React.FC = () => {
  const { currentUser, userProfile, hasPermission, isSuperAdmin } = useAuth();
  const { companyProfile } = useCompany();
  const userName = userProfile?.displayName || currentUser?.email || 'Admin';

  const canCreate = isSuperAdmin || hasPermission('sold.create');
  const canEdit = isSuperAdmin || hasPermission('sold.edit');

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [searchParams, setSearchParams] = useSearchParams();

  const [allCars, setAllCars] = useState<Product[]>([]);
  const [tradeIns, setTradeIns] = useState<TradeInSubmission[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCar, setEditingCar] = useState<Product | null>(null);
  const [prefill, setPrefill] = useState<(Partial<SoldVehicleFormValues> & { carLocked?: boolean }) | null>(null);
  const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'info' });

  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const showToast = (message: string, severity: ToastState['severity']) => setToast({ open: true, message, severity });

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const [cars, tradeInList, employeeList] = await Promise.all([
        ProductService.getProductsForDropdown(),
        TradeInService.getAll(),
        EmployeeService.getAll(),
      ]);
      setAllCars(cars);
      setTradeIns(tradeInList);
      setEmployees(employeeList.filter(e => e.status === 'Active'));
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to fetch vehicles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const soldCars = useMemo(() => allCars.filter(c => c.status === 'Sold'), [allCars]);
  const eligibleCars = useMemo(() => allCars.filter(c => c.status === 'In Stock' || c.status === 'Reserved'), [allCars]);

  // Accepted trade-ins whose value hasn't already been credited to a sale —
  // plus whichever one is already linked to the car currently being edited,
  // so re-opening an existing sale doesn't drop its trade-in selection.
  const eligibleTradeIns = useMemo(() => {
    const base = tradeIns.filter(t => t.status === 'Accepted' && !t.appliedToSaleCarId);
    if (editingCar?.tradeInId) {
      const linked = tradeIns.find(t => t.id === editingCar.tradeInId);
      if (linked && !base.some(t => t.id === linked.id)) return [linked, ...base];
    }
    return base;
  }, [tradeIns, editingCar]);

  const totalRevenue = useMemo(
    () => soldCars.reduce((sum, c) => sum + (c.finalSalePrice || 0), 0),
    [soldCars]
  );

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return soldCars;
    return soldCars.filter(c =>
      c.name?.toLowerCase().includes(q) ||
      c.sku?.toLowerCase().includes(q) ||
      c.buyerName?.toLowerCase().includes(q) ||
      c.buyerPhone?.toLowerCase().includes(q)
    );
  }, [soldCars, searchQuery]);

  const totalFiltered = filtered.length;
  const totalPages = Math.ceil(totalFiltered / PER_PAGE);
  const pageItems = filtered.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearchQuery(searchInput);
      setCurrentPage(1);
    }
  };

  // ── Deep link from a Reservation: /admin/sold-vehicles?reservationId=... ──
  useEffect(() => {
    const reservationId = searchParams.get('reservationId');
    if (!reservationId || loading) return;

    (async () => {
      try {
        const reservation = await ReservationService.getById(reservationId);
        if (!reservation || !reservation.carId) {
          showToast('That reservation could not be found', 'error');
          return;
        }
        setEditingCar(null);
        setPrefill({
          carId: reservation.carId,
          carLocked: true,
          buyerName: reservation.customerName,
          buyerPhone: reservation.customerPhone,
          buyerEmail: reservation.customerEmail || '',
          finalSalePrice: String(reservation.finalSalePrice || reservation.carPrice || ''),
          depositAmount: String(reservation.depositAmount || ''),
          paymentMethod: reservation.paymentMethod || 'Cash',
          deliveryMethod: reservation.deliveryMethod || '',
          saleNotes: '',
        });
        setIsDialogOpen(true);
      } catch (err: any) {
        showToast('Failed to load reservation: ' + (err.message || 'Unknown error'), 'error');
      } finally {
        searchParams.delete('reservationId');
        setSearchParams(searchParams, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const openCreate = () => {
    setEditingCar(null);
    setPrefill(null);
    setIsDialogOpen(true);
  };

  const openEdit = (car: Product) => {
    setEditingCar(car);
    setPrefill({
      carId: car.id,
      carLocked: true,
      buyerName: car.buyerName || '',
      buyerPhone: car.buyerPhone || '',
      buyerEmail: car.buyerEmail || '',
      buyerIdNumber: car.buyerIdNumber || '',
      buyerAddress: car.buyerAddress || '',
      finalSalePrice: String(car.finalSalePrice || ''),
      depositAmount: String(car.depositAmount || ''),
      paymentMethod: car.paymentMethod || 'Cash',
      deliveryMethod: car.deliveryMethod || '',
      deliveryDate: car.deliveryDate || '',
      saleNotes: car.saleNotes || '',
      salespersonId: car.salespersonId || '',
      tradeInId: car.tradeInId || '',
      financingProvider: car.financingProvider || '',
      financingTermMonths: car.financingTermMonths ? String(car.financingTermMonths) : '',
      monthlyInstallment: car.monthlyInstallment ? String(car.monthlyInstallment) : '',
      registrationTransferStatus: car.registrationTransferStatus || 'Not Started',
      warrantyMonths: car.warrantyMonths ? String(car.warrantyMonths) : '',
      warrantyNotes: car.warrantyNotes || '',
      documents: car.documents || [],
    });
    setIsDialogOpen(true);
  };

  const handlePrintReceipt = (car: Product) => {
    SaleReceiptGenerator.generate(car, companyProfile);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingCar(null);
    setPrefill(null);
  };

  const handleSave = async (values: SoldVehicleFormValues) => {
    const car = editingCar || allCars.find(c => c.id === values.carId);
    if (!car) {
      showToast('Could not find the selected vehicle — please reselect it and try again', 'error');
      return;
    }
    try {
      const selectedTradeIn = values.tradeInId ? tradeIns.find(t => t.id === values.tradeInId) : undefined;
      const selectedSalesperson = values.salespersonId ? employees.find(e => e.id === values.salespersonId) : undefined;
      await SoldVehicleService.recordSale(
        {
          carId: values.carId,
          carName: car.name,
          buyerName: values.buyerName,
          buyerPhone: values.buyerPhone,
          buyerEmail: values.buyerEmail || undefined,
          buyerIdNumber: values.buyerIdNumber || undefined,
          buyerAddress: values.buyerAddress || undefined,
          finalSalePrice: parseFloat(values.finalSalePrice) || 0,
          depositAmount: parseFloat(values.depositAmount) || 0,
          paymentMethod: values.paymentMethod || undefined,
          deliveryMethod: values.deliveryMethod || undefined,
          deliveryDate: values.deliveryDate || undefined,
          saleNotes: values.saleNotes || undefined,
          salespersonId: values.salespersonId || undefined,
          salespersonName: selectedSalesperson?.fullName || undefined,
          reservationId: car.reservationId || undefined,
          tradeInId: values.tradeInId || undefined,
          tradeInValue: selectedTradeIn?.offeredPrice || 0,
          financingProvider: values.financingProvider || undefined,
          financingTermMonths: parseInt(values.financingTermMonths, 10) || undefined,
          monthlyInstallment: parseFloat(values.monthlyInstallment) || undefined,
          registrationTransferStatus: (values.registrationTransferStatus || 'Not Started') as any,
          warrantyMonths: parseInt(values.warrantyMonths, 10) || undefined,
          warrantyNotes: values.warrantyNotes || undefined,
          documents: values.documents,
        },
        currentUser?.uid || '',
        userName
      );
      handleDialogClose();
      showToast('Sale recorded successfully', 'success');
      await load();
    } catch (err: any) {
      showToast('Failed to record sale: ' + (err.message || 'Unknown error'), 'error');
    }
  };

  if (loading && allCars.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px" flexDirection="column">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ mt: 2 }}>Loading sold vehicles...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: { md: '100%' }, display: 'flex', flexDirection: 'column', overflow: { xs: 'visible', md: 'hidden' }, p: { xs: 2, sm: 3 } }}>
      <Box sx={{ flexShrink: 0 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <SellIcon sx={{ fontSize: { xs: 28, md: 32 }, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
                Sold Vehicles
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Every vehicle actually sold — imported or pre-owned — with buyer and payment details
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button onClick={load} variant="outlined" startIcon={<RefreshIcon />} disabled={loading}>Refresh</Button>
            {canCreate && (
              <Button onClick={openCreate} variant="contained" startIcon={<AddIcon />}>Record New Sale</Button>
            )}
          </Stack>
        </Stack>

        {errorMsg && (
          <Alert severity="error" sx={{ mb: 3 }} action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
            {errorMsg}
          </Alert>
        )}

        <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>Total Sold</Typography>
                <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' } }}>{soldCars.length}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>Total Revenue</Typography>
                <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' } }}>{totalRevenue.toLocaleString()}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
          <TextField
            placeholder="Search by car, VIN, or buyer... (Press Enter)"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={handleSearchKeyPress}
            fullWidth
            size="small"
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          />
        </Paper>
      </Box>

      <Box sx={{ flex: 1, overflow: { xs: 'visible', md: 'auto' }, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>
        {pageItems.length === 0 && !loading && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
            <Card sx={{ width: '100%', maxWidth: 500 }}>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <SellIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  {searchQuery ? 'No Sold Vehicles Match Your Search' : 'No Vehicles Sold Yet'}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                  {searchQuery ? 'Try a different search term.' : 'Record a sale to see it listed here.'}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        )}

        {pageItems.length > 0 && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>
            <Box sx={{ flex: 1, overflow: 'auto', mb: 2, minHeight: { xs: 'auto', md: 0 } }}>
              <SoldVehicleTable cars={pageItems} onEdit={openEdit} onPrintReceipt={handlePrintReceipt} isDark={isDark} canEdit={canEdit} />
            </Box>
            <Paper elevation={2} sx={{ flexShrink: 0, borderRadius: 1 }}>
              <Box sx={{ py: 2, px: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.5, sm: 3 }} alignItems="center" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Showing {totalFiltered === 0 ? 0 : (currentPage - 1) * PER_PAGE + 1}–{Math.min(currentPage * PER_PAGE, totalFiltered)} of {totalFiltered} vehicles
                  </Typography>
                  <Pagination
                    count={Math.max(1, totalPages)}
                    page={currentPage}
                    onChange={(_e, page) => setCurrentPage(page)}
                    color="primary"
                    size={isMobile ? 'small' : 'medium'}
                    showFirstButton
                    showLastButton
                    disabled={totalPages <= 1}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Page {currentPage} of {Math.max(1, totalPages)}
                  </Typography>
                </Stack>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>

      <SoldVehicleDialog
        isOpen={isDialogOpen}
        eligibleCars={editingCar ? [editingCar, ...eligibleCars] : eligibleCars}
        eligibleTradeIns={eligibleTradeIns}
        employees={employees}
        prefill={prefill}
        onClose={handleDialogClose}
        onSave={handleSave}
      />

      <ToastNotification open={toast.open} message={toast.message} severity={toast.severity} onClose={() => setToast(t => ({ ...t, open: false }))} />
    </Box>
  );
};

export default SoldVehiclesManagement;
